import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Modal from '../Modal';
import useGstInvoiceExport from '../../hooks/fetch-hooks/gst-invoice-export/use-gst-invoice-export';

const GstInvoiceExportModal = ({ onClose }) => {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [invoiceTypes, setInvoiceTypes] = useState({ SERVICE: true, DELIVERY: true });
    const [documentTypes, setDocumentTypes] = useState({ INVOICE: true, CREDIT_NOTE: true });
    const [useStoredTaxes, setUseStoredTaxes] = useState(false);
    const [validationError, setValidationError] = useState('');

    const { mutate: exportGst, isLoading } = useGstInvoiceExport();

    const toggleInvoiceType = (type) => {
        setInvoiceTypes((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    const toggleDocumentType = (type) => {
        setDocumentTypes((prev) => ({ ...prev, [type]: !prev[type] }));
    };

    const handleDownload = () => {
        setValidationError('');

        if (!from || !to) {
            setValidationError('Both From and To dates are required.');
            return;
        }
        if (from > to) {
            setValidationError('"From" date must be on or before "To" date.');
            return;
        }

        const selectedInvoiceTypes = Object.entries(invoiceTypes)
            .filter(([, checked]) => checked)
            .map(([type]) => type);
        const selectedDocumentTypes = Object.entries(documentTypes)
            .filter(([, checked]) => checked)
            .map(([type]) => type);

        if (selectedInvoiceTypes.length === 0) {
            setValidationError('Select at least one invoice type.');
            return;
        }
        if (selectedDocumentTypes.length === 0) {
            setValidationError('Select at least one document type.');
            return;
        }

        exportGst({
            from,
            to,
            invoiceType: selectedInvoiceTypes.join(','),
            documentType: selectedDocumentTypes.join(','),
            useStoredTaxes: useStoredTaxes || undefined,
        });
    };

    const checkboxClass = 'w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500';
    const labelClass = 'ml-2 text-sm text-gray-700';

    return (
        <Modal title="Export GST Invoices" onClose={onClose}>
            <div className="space-y-5">
                {/* Date range — mirror orders.jsx pattern */}
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Date Range</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <input
                                type="date"
                                value={from}
                                onChange={(e) => {
                                    setFrom(e.target.value);
                                    if (to && e.target.value > to) setTo('');
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                            <input
                                type="date"
                                value={to}
                                min={from || undefined}
                                disabled={!from}
                                onChange={(e) => setTo(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>
                </div>

                {/* Invoice type */}
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Invoice Type</p>
                    <div className="flex gap-5">
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                checked={invoiceTypes.SERVICE}
                                onChange={() => toggleInvoiceType('SERVICE')}
                            />
                            <span className={labelClass}>SERVICE</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                checked={invoiceTypes.DELIVERY}
                                onChange={() => toggleInvoiceType('DELIVERY')}
                            />
                            <span className={labelClass}>DELIVERY</span>
                        </label>
                    </div>
                </div>

                {/* Document type */}
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Document Type</p>
                    <div className="flex gap-5">
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                checked={documentTypes.INVOICE}
                                onChange={() => toggleDocumentType('INVOICE')}
                            />
                            <span className={labelClass}>Invoice</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                className={checkboxClass}
                                checked={documentTypes.CREDIT_NOTE}
                                onChange={() => toggleDocumentType('CREDIT_NOTE')}
                            />
                            <span className={labelClass}>Credit Note</span>
                        </label>
                    </div>
                </div>

                {/* Use stored taxes */}
                <div>
                    <label className="inline-flex items-center gap-2">
                        <input
                            type="checkbox"
                            className={checkboxClass}
                            checked={useStoredTaxes}
                            onChange={(e) => setUseStoredTaxes(e.target.checked)}
                        />
                        <span className="text-sm text-gray-700">
                            Use stored taxes{' '}
                            <span className="text-gray-400">(off = recompute GST from address state, recommended)</span>
                        </span>
                    </label>
                </div>

                {/* Validation error */}
                {validationError && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                        {validationError}
                    </p>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm font-medium text-white-50 bg-green-600 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Generating…' : 'Download .xlsx'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

GstInvoiceExportModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default GstInvoiceExportModal;
